import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Globe, Plus, Trash2 } from 'lucide-react';
import {
  type BillingEntityDomainClaim,
  type ListBillingEntityDomainClaimsResponse,
} from '@reflex/shared';
import {
  getListCompanyDomainClaimsQueryKey,
  useCreateCompanyDomainClaim,
  useListCompanyDomainClaims,
  useRemoveCompanyDomainClaim,
  useUpdateCompanyDomainClaimPolicy,
  useVerifyCompanyDomainClaim,
} from '@reflex/ui/client/generated/company';
import { Button } from '@reflex/ui/components/ui/button';
import { Input } from '@reflex/ui/components/ui/input';
import { Label } from '@reflex/ui/components/ui/label';
import { Switch } from '@reflex/ui/components/ui/switch';
import { ConfirmDialog } from '@reflex/ui/components/shared/ConfirmDialog';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { CanGuard } from '@reflex/ui/client/permissions';
import { PageDescription, PanelTitle, SectionLabel } from '@reflex/ui/components/layout/Typography';
import { formatDateTime } from '@reflex/ui/lib/format';
import { AsyncContent } from '@/components/shared/AsyncContent';
import { ConfigBadge } from '@/components/status/ConfigBadge';
import { CopyRow } from '@/components/setup/CopyRow';
import { useAuth } from '@/hooks/auth/useAuth';
import { DOMAIN_CLAIM_STATUS_BADGE } from '@/lib/status-colors';
import { AutoJoinDestinationSelect } from './AutoJoinDestinationSelect';
import { claimErrorCopy } from './claim-error-copy';
import { claimDomainSchema, type ClaimDomainForm } from './claim-domain-schema';
import { toastVerifyOutcome } from './domain-claim-toasts';

/**
 * Company-owned domain claims, a section of the Company page. The company
 * comes from the caller's active organization — a customer request never
 * names one — so this takes the organization id only to key the cache per
 * active organization. Reading takes `company:read`; every write takes
 * `company:manage`, so a reader sees the claims without the controls.
 */
export function CompanyDomainClaims({ organizationId }: { organizationId: string }) {
  const queryClient = useQueryClient();
  const canManage = useAuth().can('company:manage');
  const listQueryKey = [...getListCompanyDomainClaimsQueryKey(), organizationId];
  const claimsQuery = useListCompanyDomainClaims({ query: { queryKey: listQueryKey } });
  const claims = claimsQuery.data?.claims ?? [];
  const organizations = claimsQuery.data?.organizations ?? [];

  function handleClaimError(err: unknown, fallbackTitle: string) {
    const copy = claimErrorCopy(err);
    if (copy) {
      toast.error(copy.title, { description: copy.description });
      return;
    }
    toast.error(fallbackTitle, {
      description: err instanceof Error ? err.message : undefined,
    });
  }

  function invalidateList() {
    void queryClient.invalidateQueries({ queryKey: listQueryKey });
  }

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<ClaimDomainForm, unknown, z.output<typeof claimDomainSchema>>({
    resolver: zodResolver(claimDomainSchema),
    defaultValues: { domain: '' },
  });
  const domainInput = useWatch({ control, name: 'domain' });

  const createMutation = useCreateCompanyDomainClaim({
    mutation: {
      onSuccess: (claim) => {
        invalidateList();
        reset();
        toast.success(`Claimed ${claim.domain}`, {
          description: 'Publish the TXT record below, then verify.',
        });
      },
      onError: (err) => handleClaimError(err, 'Failed to claim domain'),
    },
  });

  const verifyMutation = useVerifyCompanyDomainClaim({
    mutation: {
      onSuccess: (result) => {
        invalidateList();
        toastVerifyOutcome(result);
      },
      onError: (err) => handleClaimError(err, 'Failed to check DNS'),
    },
  });

  const policyMutation = useUpdateCompanyDomainClaimPolicy({
    mutation: {
      onSuccess: () => {
        invalidateList();
        toast.success('Routing policy updated');
      },
      onError: (err) => handleClaimError(err, 'Failed to update routing policy'),
    },
  });

  const removeMutation = useRemoveCompanyDomainClaim({
    mutation: {
      onSuccess: () => {
        invalidateList();
        toast.success('Domain claim removed');
      },
      onError: (err) => handleClaimError(err, 'Failed to remove domain claim'),
    },
  });

  const anyPending =
    createMutation.isPending ||
    verifyMutation.isPending ||
    policyMutation.isPending ||
    removeMutation.isPending;

  return (
    <section className="space-y-5" data-testid="company-domain-claims">
      <div>
        <PanelTitle as="h2" truncate={false}>
          Verified domains
        </PanelTitle>
        <PageDescription>
          Prove your company controls an email domain, then choose what that proof routes. Routing
          applies to your company as a whole. Reflex re-checks the record daily, and routing stops
          if the proof is lost.
        </PageDescription>
      </div>

      <CanGuard permission="company:manage">
        <SectionCard
          title="Claim a domain"
          description="Enter the email domain your company controls. Claiming issues the TXT challenge to publish; nothing routes until the domain is verified."
          icon={<Plus />}
          data-testid="claim-domain-section"
        >
          <form
            onSubmit={handleSubmit((values) => createMutation.mutate({ data: values }))}
            className="flex flex-col gap-2"
          >
            <div className="flex max-w-xl items-end gap-2">
              <div className="flex flex-1 flex-col gap-2">
                <Label htmlFor="claim-domain-input">Domain</Label>
                <Input
                  id="claim-domain-input"
                  placeholder="example.com"
                  autoComplete="off"
                  spellCheck={false}
                  maxLength={253}
                  aria-invalid={errors.domain ? true : undefined}
                  {...register('domain')}
                />
              </div>
              <Button
                type="submit"
                disabled={!domainInput.trim() || createMutation.isPending}
                aria-label="Claim domain"
              >
                {createMutation.isPending ? 'Claiming…' : 'Claim domain'}
              </Button>
            </div>
            {errors.domain && (
              <p role="alert" className="text-xs text-destructive">
                {errors.domain.message}
              </p>
            )}
          </form>
        </SectionCard>
      </CanGuard>

      <SectionCard
        title="Claimed domains"
        description="Each claim carries its DNS challenge and, once verified, the routing choices for your company's organizations."
        icon={<Globe />}
        data-testid="domain-claims-section"
      >
        <AsyncContent
          isLoading={claimsQuery.isLoading}
          isError={claimsQuery.isError}
          loadingMessage="Loading domain claims…"
          errorMessage="Could not load domain claims. Refresh the page to try again."
        >
          {claims.length > 0 ? (
            <ul className="flex flex-col gap-4">
              {claims.map((claim) => (
                <BillingEntityDomainClaimRow
                  key={claim.id}
                  claim={claim}
                  organizations={organizations}
                  canManage={canManage}
                  anyPending={anyPending}
                  onVerify={() => verifyMutation.mutate({ claimId: claim.id })}
                  onPolicyChange={(data) => policyMutation.mutate({ claimId: claim.id, data })}
                  onRemove={() => removeMutation.mutate({ claimId: claim.id })}
                />
              ))}
            </ul>
          ) : (
            <EmptyState
              variant="bordered"
              title="No domains claimed yet."
              description={
                canManage
                  ? 'Claim a domain above to prove your company controls it, then route SSO discovery and auto-join through the proof.'
                  : 'A Company Manager can claim a domain to prove your company controls it.'
              }
            />
          )}
        </AsyncContent>
      </SectionCard>
    </section>
  );
}

interface BillingEntityDomainClaimRowProps {
  claim: BillingEntityDomainClaim;
  organizations: ListBillingEntityDomainClaimsResponse['organizations'];
  /** The caller holds `company:manage`; without it the row is read-only. */
  canManage: boolean;
  /** Some claim mutation is in flight; disable the row's write controls. */
  anyPending: boolean;
  onVerify: () => void;
  onPolicyChange: (data: {
    ssoDiscoveryEnabled?: boolean;
    autoJoinOrganizationId?: string | null;
  }) => void;
  onRemove: () => void;
}

/**
 * One claim: identity and status, the DNS challenge to publish, and the
 * routing policy controls (enabled only while the claim is verified).
 */
function BillingEntityDomainClaimRow({
  claim,
  organizations,
  canManage,
  anyPending,
  onVerify,
  onPolicyChange,
  onRemove,
}: BillingEntityDomainClaimRowProps) {
  const verified = claim.status === 'verified';
  // A grandfathered claim is platform-approved only by migration; its
  // company can still prove it, so it gets the DNS controls.
  const platformApproved = claim.method === 'platform-approved' && !claim.grandfathered;
  const writeDisabled = !canManage || anyPending;

  const statusNote = claim.grandfathered
    ? 'Carried over from your earlier domain settings, so routing continues. Publish the TXT record and verify to also let your identity provider sign in existing accounts on this domain.'
    : platformApproved
      ? 'Platform approved: Reflex verified this domain for your dedicated deployment. It is not checked against public DNS.'
      : claim.status === 'pending'
        ? 'Waiting for the TXT record. Publish it with your DNS provider, then verify.'
        : claim.status === 'lost'
          ? 'The TXT record is no longer visible in public DNS. Routing stopped; republish the record and verify to restore it.'
          : claim.verifiedAt
            ? `Verified ${formatDateTime(claim.verifiedAt)}.`
            : 'Verified.';

  return (
    <li
      className="flex flex-col gap-4 rounded-md border p-4"
      data-testid={`domain-claim-${claim.id}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{claim.domain}</span>
        <ConfigBadge config={DOMAIN_CLAIM_STATUS_BADGE} value={claim.status} />
        <div className="ml-auto flex items-center gap-2">
          {canManage && !platformApproved && (
            <Button
              variant="outline"
              size="sm"
              onClick={onVerify}
              disabled={anyPending}
              aria-label={`Verify ${claim.domain} now`}
            >
              Verify now
            </Button>
          )}
          {canManage && (
            <ConfirmDialog
              title={`Remove the claim on ${claim.domain}?`}
              description="SSO discovery and auto-join for this domain stop immediately. Members who already joined keep their access. You can claim the domain again later."
              confirmLabel="Remove claim"
              onConfirm={onRemove}
              trigger={
                <Button
                  variant="outline"
                  size="sm"
                  disabled={anyPending}
                  aria-label={`Remove claim on ${claim.domain}`}
                >
                  <Trash2 className="icon-sm" /> Remove
                </Button>
              }
            />
          )}
        </div>
      </div>

      <p className="text-xs text-muted-foreground">{statusNote}</p>

      {!platformApproved && (
        <div className="flex flex-col gap-3" data-testid={`domain-claim-dns-${claim.id}`}>
          <SectionLabel as="h3">DNS record to publish</SectionLabel>
          <CopyRow label="Record name" value={claim.recordName} />
          <CopyRow label="Record type" value="TXT" />
          {/* The challenge token is proof material published in public DNS by
              design, so it renders in the clear like the other row values. */}
          <CopyRow label="Record value" value={claim.challengeToken} />
        </div>
      )}

      <div className="flex flex-col gap-3">
        <SectionLabel as="h3">Routing</SectionLabel>
        {!verified && (
          <p className="text-xs text-muted-foreground">
            {claim.status === 'lost'
              ? 'Routing has stopped. You can still switch these off; turning them back on needs the domain verified again.'
              : 'You can turn routing on once the domain is verified.'}
          </p>
        )}
        <div className="flex max-w-md items-center justify-between gap-4">
          <Label htmlFor={`sso-discovery-${claim.id}`}>SSO discovery</Label>
          {/* Turning routing OFF is always allowed, matching the server:
              only enabling requires proof. Disabling a setting on a claim
              whose proof just lapsed is exactly what an admin reaches for. */}
          <Switch
            id={`sso-discovery-${claim.id}`}
            checked={claim.ssoDiscoveryEnabled}
            disabled={(!verified && !claim.ssoDiscoveryEnabled) || writeDisabled}
            onCheckedChange={(next) => onPolicyChange({ ssoDiscoveryEnabled: next })}
            aria-label={`SSO discovery for ${claim.domain}`}
          />
        </div>
        <div className="flex max-w-md flex-col gap-2">
          <Label htmlFor={`auto-join-${claim.id}`}>Auto-join destination</Label>
          <AutoJoinDestinationSelect
            id={`auto-join-${claim.id}`}
            value={claim.autoJoinOrganizationId}
            organizations={organizations}
            disabled={(!verified && claim.autoJoinOrganizationId === null) || writeDisabled}
            ariaLabel={`Auto-join destination for ${claim.domain}`}
            onChange={(autoJoinOrganizationId) => onPolicyChange({ autoJoinOrganizationId })}
          />
          <p className="text-xs text-muted-foreground">
            New sign-ups with a {claim.domain} email join this organization automatically, and it
            becomes an eligible sign-in destination for the domain. Pick any organization your
            company owns.
          </p>
        </div>
      </div>
    </li>
  );
}
